import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Copy, Receipt } from 'lucide-react';
import Table from '../../components/Table/Table';
import Pagination from 'src/components/Pagination';
import Status from 'src/components/Status/Status';
import PageShell from 'src/components/PageShell';
import BackLink from 'src/components/BackLink';
import { SectionHeader, EmptyState, NairaText } from 'src/components/ui-x';
import useAlert from 'src/hooks/use-alert';
import { useTransactionsQuery } from 'src/lib/endpoints/wallet/wallet.api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

const TransactionTable = () => {
  const siblingCount = 1;
  const pageColumn = 1;
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(1000);
  const { transactions } = useTransactionsQuery(page, pageSize);
  const { state, open: openAlert, close: closeAlert, Alert } = useAlert();
  const location = useLocation();

  const standalone = location.pathname === '/transactions';

  const handlePageChange = useCallback(
    (_page) => {
      setPage(_page - 1);
    },
    [page]
  );

  const tableHeader = useMemo(
    () => [
      {
        title: 'No.',
        key: 'no',
      },
      {
        title: 'Txn ID',
        key: 'id',
      },
      {
        title: 'Txn Ref',
        key: 'txn_ref',
      },
      {
        title: 'Type',
        key: 'type',
      },
      {
        title: 'Amount',
        key: 'amount',
        numeric: true,
      },
      {
        title: 'Created At',
        key: 'created',
      },
      {
        title: 'Updated At',
        key: 'update',
      },
      {
        title: 'Status',
        key: 'status',
      },
    ],
    []
  );

  const copyCell = (value, label) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className='flex items-center gap-2 hover:cursor-pointer'>
            <span className='font-mono text-xs text-muted-foreground'>
              {value
                ? value.toString().slice(0, 10) +
                  (value.toString().length > 10 ? '…' : '')
                : '-'}
            </span>
            {value && (
              <button
                type='button'
                aria-label={`Copy ${label}`}
                className='text-muted-foreground transition-colors hover:text-brand-orange'
                onClick={() => {
                  navigator.clipboard.writeText(value ?? '');
                  openAlert({
                    ...state,
                    title: 'Copied',
                    variant: 'success',
                    active: true,
                  });
                }}
              >
                <Copy className='h-3.5 w-3.5' />
              </button>
            )}
          </div>
        </TooltipTrigger>
        {value && (
          <TooltipContent className='bg-card'>
            <p className='font-mono text-xs'>{value}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  const modifiedData = useMemo(() => {
    return transactions?.data.map((each, idx) => [
      <span className='tabular-nums text-muted-foreground'>
        {page * pageSize + (idx + 1)}
      </span>,
      copyCell(each?.id, 'transaction ID'),
      copyCell(each?.transfer_id, 'transaction reference'),
      <span className='capitalize text-muted-foreground'>{each.type}</span>,
      <NairaText value={Number(each.amount ?? 0)} className='font-semibold' />,
      <span className='tabular-nums text-muted-foreground'>
        {format(new Date(each?.created_at ?? Date.now()), 'EEE, MMM d')}
      </span>,
      <span className='tabular-nums text-muted-foreground'>
        {format(new Date(each?.updated_at ?? Date.now()), 'EEE, MMM d')}
      </span>,
      <Status label={each?.status?.toLowerCase()} />,
    ]);
  }, [transactions]);

  useEffect(() => {
    setTotalCount(transactions?.total ?? 0);
  }, [transactions]);

  const table = (
    <Table
      header={tableHeader}
      eyebrow={standalone ? undefined : 'Activity'}
      label={standalone ? undefined : 'Transactions'}
      column={modifiedData ?? []}
      defaultDisplay={
        <EmptyState
          className='border-0 py-10'
          icon={<Receipt className='h-6 w-6' />}
          title='No transactions yet'
          description='Engage with Droomwork services and your transactions will show up here.'
        />
      }
    />
  );

  const body = (
    <>
      {table}

      <Pagination
        rangeStart={
          totalCount !== 0 ? (page || 0) * ((pageSize || 0) * pageColumn) + 1 : 0
        }
        totalCount={totalCount}
        currentPage={(page || 0) + 1}
        pageSize={pageSize}
        pageColumn={pageColumn}
        siblingCount={siblingCount}
        onPageChange={handlePageChange}
      />

      <span className='fixed right-3 top-20 z-30'>
        <Alert />
      </span>
    </>
  );

  if (!standalone) return <div className='space-y-6'>{body}</div>;

  return (
    <PageShell wide>
      <BackLink to='/wallet' label='Wallet' />
      <SectionHeader
        eyebrow='Activity'
        title='Transactions'
        description='Every payment in and out of your Droomwork wallet.'
      />
      {body}
    </PageShell>
  );
};

export default TransactionTable;
